import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Paper, Box, Button, Switch, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, TextField, Tooltip
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { fetchPreferences, updatePreferences } from '../../store/slices/notificationSlice';
import { CANONICAL_CATEGORIES } from '../../constants/notifications';

const NotificationSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { preferences, preferencesLoading } = useSelector(state => state.notifications || {});

  // Browser permission state for blocked notice
  const [browserPermission, setBrowserPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );

  useEffect(() => {
    dispatch(fetchPreferences());
  }, [dispatch]);

  // Refresh permission state on mount (may change externally)
  useEffect(() => {
    if (typeof Notification !== 'undefined') {
      setBrowserPermission(Notification.permission);
    }
  }, []);

  // Merge backend preferences with canonical defaults (lazy category handling)
  const getMergedCategories = () => {
    const backendCats = preferences?.categories || [];
    return CANONICAL_CATEGORIES.map(cat => {
      const existing = backendCats.find(c => c.category === cat.key);
      if (existing) return existing;
      // Default row for categories missing from backend
      return {
        category: cat.key,
        in_app: true,
        push: cat.pushDefault,
        email: cat.channels.includes('email') ? cat.pushDefault : false,
        sms: cat.channels.includes('sms') ? false : false,
      };
    });
  };

  const handleToggle = (category, channel) => {
    // Don't allow toggling locked categories
    const catDef = CANONICAL_CATEGORIES.find(c => c.key === category);
    if (catDef?.pushLocked && channel === 'push') return;

    const merged = getMergedCategories();
    const updated = merged.map(cat => {
      if (cat.category === category) {
        return { ...cat, [channel]: !cat[channel] };
      }
      return cat;
    });
    dispatch(updatePreferences({
      quiet_hours: preferences?.quiet_hours || { enabled: false, start: '23:00', end: '07:00' },
      categories: updated,
    }));
  };

  const handleQuietHoursToggle = () => {
    const qh = preferences?.quiet_hours || { enabled: false, start: '23:00', end: '07:00' };
    dispatch(updatePreferences({
      quiet_hours: { ...qh, enabled: !qh.enabled },
      categories: getMergedCategories(),
    }));
  };

  const handleQuietHoursTimeChange = (field, value) => {
    const qh = preferences?.quiet_hours || { enabled: false, start: '23:00', end: '07:00' };
    dispatch(updatePreferences({
      quiet_hours: { ...qh, [field]: value },
      categories: getMergedCategories(),
    }));
  };

  if (preferencesLoading || !preferences) {
    return (
      <Container maxWidth="md" sx={{ mt: 3 }}>
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      </Container>
    );
  }

  const quietHours = preferences.quiet_hours || { enabled: false, start: '23:00', end: '07:00' };
  const mergedCategories = getMergedCategories();

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <IconButton onClick={() => navigate(-1)}><ArrowBack /></IconButton>
        <Typography variant="h5">Notification Settings</Typography>
      </Box>

      {/* Browser blocked notice */}
      {browserPermission === 'denied' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Push notifications are blocked in your browser. To enable, open your browser settings and allow notifications for jyotishsastra.com.
        </Alert>
      )}

      {/* Quiet Hours */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Quiet Hours</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            label="From"
            type="time"
            size="small"
            value={quietHours.start || '23:00'}
            onChange={(e) => handleQuietHoursTimeChange('start', e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={!quietHours.enabled}
          />
          <Typography>to</Typography>
          <TextField
            label="Until"
            type="time"
            size="small"
            value={quietHours.end || '07:00'}
            onChange={(e) => handleQuietHoursTimeChange('end', e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={!quietHours.enabled}
          />
          <Switch
            checked={quietHours.enabled || false}
            onChange={handleQuietHoursToggle}
          />
        </Box>
        <Alert severity="info" sx={{ mt: 1 }}>
          Incoming calls and low-balance alerts bypass quiet hours.
        </Alert>
      </Paper>

      {/* Category Preferences */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell align="center"><strong>In-App</strong></TableCell>
              <TableCell align="center"><strong>Push</strong></TableCell>
              <TableCell align="center"><strong>Email</strong></TableCell>
              <TableCell align="center"><strong>SMS</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {CANONICAL_CATEGORIES.map((catDef) => {
              const pref = mergedCategories.find(c => c.category === catDef.key) || {};
              return (
                <TableRow key={catDef.key}>
                  <TableCell>{catDef.label}</TableCell>
                  <TableCell align="center">
                    <Switch checked disabled size="small" />
                  </TableCell>
                  <TableCell align="center">
                    {catDef.pushLocked ? (
                      <Tooltip title="Consultation notifications cannot be disabled — they are essential for live sessions.">
                        <span>
                          <Switch checked disabled size="small" />
                        </span>
                      </Tooltip>
                    ) : (
                      <Switch
                        checked={pref.push || false}
                        onChange={() => handleToggle(catDef.key, 'push')}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {catDef.channels.includes('email') ? (
                      <Switch
                        checked={pref.email || false}
                        onChange={() => handleToggle(catDef.key, 'email')}
                        size="small"
                      />
                    ) : (
                      <Switch checked={false} disabled size="small" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {catDef.channels.includes('sms') ? (
                      <Switch
                        checked={pref.sms || false}
                        onChange={() => handleToggle(catDef.key, 'sms')}
                        size="small"
                      />
                    ) : (
                      <Switch checked={false} disabled size="small" />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Alert severity="info" sx={{ mt: 2 }}>
        In-App notifications are always enabled. Critical notifications bypass all preferences.
      </Alert>
    </Container>
  );
};

export default NotificationSettings;
